import { getBackendUrl } from '../utils/utils';
import { createLogger } from '../utils/logger';
import { api } from '../components/role-play/apiService';
import { AppUser } from '../types';

const logger = createLogger('userService');

export async function fetchUserById(userId: string): Promise<AppUser> {
  return api.get<AppUser>(`/user/${userId}`);
}

export interface UserSummary {
  id: string;
  display_name: string;
  email?: string;
  role?: string;
  organization_id?: string;
}

export interface UsersListResponse {
  items: UserSummary[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

const BATCH_USERS_CHUNK_SIZE = 100;

export async function batchGetUsers(token: string, userIds: string[]): Promise<UserSummary[]> {
  if (userIds.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < userIds.length; i += BATCH_USERS_CHUNK_SIZE) {
    chunks.push(userIds.slice(i, i + BATCH_USERS_CHUNK_SIZE));
  }
  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const res = await fetch(`${getBackendUrl()}/user/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(chunk),
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      return res.json() as Promise<UserSummary[]>;
    }),
  );
  return results.flat();
}

export async function searchUsers(
  token: string,
  search: string,
  page = 1,
  pageSize = 20,
  organizationId?: string,
): Promise<UsersListResponse> {
  const q = new URLSearchParams({ search, page: String(page), page_size: String(pageSize) });
  if (organizationId) {
    q.set('organization_id', organizationId);
  }
  const res = await fetch(`${getBackendUrl()}/user?${q.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to search users');
  return res.json() as Promise<UsersListResponse>;
}

export interface UserFTUEUpdate {
  mic_setup_completed?: boolean;
  interactive_tutorial_completed?: boolean;
}

export interface UserPolicyUpdate {
  policy_accepted_version?: string;
}

const CACHE_DURATION = 24 * 60 * 60 * 1000;

const POLICY_CACHE_KEY = 'policy_acceptance_cache';
const POLICY_VERSION_SESSION_KEY = 'policy_current_version';

/**
 * Fetch the current required policy version from the backend (single source of truth).
 * Result is cached in sessionStorage for the duration of the browser session.
 */
const getCurrentPolicyVersion = async (): Promise<string> => {
  const cached = sessionStorage.getItem(POLICY_VERSION_SESSION_KEY);
  if (cached) return cached;

  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/config/policy-version`);
  if (!response.ok) throw new Error('Failed to fetch policy version from server');

  const data = await response.json();
  const version: string = data.policy_version;
  sessionStorage.setItem(POLICY_VERSION_SESSION_KEY, version);
  return version;
};

interface PolicyCache {
  [userId: string]: {
    version: string;
    timestamp: number;
  };
}

const getPolicyFromCache = (userId: string): string | null => {
  try {
    const cacheStr = localStorage.getItem(POLICY_CACHE_KEY);
    if (!cacheStr) return null;

    const cache: PolicyCache = JSON.parse(cacheStr);
    const entry = cache[userId];
    if (!entry) return null;

    if (Date.now() - entry.timestamp > CACHE_DURATION) return null;

    return entry.version;
  } catch {
    return null;
  }
};

const savePolicyToCache = (userId: string, version: string): void => {
  try {
    const cacheStr = localStorage.getItem(POLICY_CACHE_KEY) || '{}';
    const cache: PolicyCache = JSON.parse(cacheStr);
    cache[userId] = { version, timestamp: Date.now() };
    localStorage.setItem(POLICY_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore cache write errors
  }
};

const FTUE_CACHE_KEY = 'ftue_status_cache_v2';

interface FTUECache {
  [userId: string]: {
    completed: boolean;
    timestamp: number;
  };
}



const getFTUEFromCache = (userId: string): boolean | null => {
  try {
    const cacheStr = localStorage.getItem(FTUE_CACHE_KEY);
    if (!cacheStr) return null;

    const cache: FTUECache = JSON.parse(cacheStr);
    const userCache = cache[userId];
    if (!userCache) return null;

    if (Date.now() - userCache.timestamp > CACHE_DURATION) return null;

    return userCache.completed;
  } catch {
    return null;
  }
};

const saveFTUEToCache = (userId: string, completed: boolean): void => {
  try {
    const cacheStr = localStorage.getItem(FTUE_CACHE_KEY) || '{}';
    const cache: FTUECache = JSON.parse(cacheStr);
    cache[userId] = { completed, timestamp: Date.now() };
    localStorage.setItem(FTUE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore cache write errors
  }
};

/**
 * Update user's FTUE status. Uses PUT response directly (no second GET).
 */
export const updateUserFTUEStatus = async (
  userId: string,
  token: string,
  update: Partial<UserFTUEUpdate>
): Promise<void> => {
  const backendUrl = getBackendUrl();

  const response = await fetch(`${backendUrl}/user/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(update),
  });

  if (!response.ok) {
    throw new Error('Failed to update user FTUE status');
  }

  // Use PUT response directly to update cache (no extra GET)
  try {
    const userData = await response.json();
    // A no-op update returns { detail: "No changes detected" } with no user fields —
    // skip the cache write rather than storing a non-boolean `completed` value.
    if (typeof userData.mic_setup_completed !== 'boolean') {
      return;
    }
    // FTUE is complete once the mic is set up. (interactive_tutorial_completed is a separate future step.)
    const ftueCompleted = userData.mic_setup_completed;
    saveFTUEToCache(userId, ftueCompleted);
  } catch {
    // If response body can't be parsed, clear user's cache entry
    try {
      const cacheStr = localStorage.getItem(FTUE_CACHE_KEY);
      if (cacheStr) {
        const cache: FTUECache = JSON.parse(cacheStr);
        delete cache[userId];
        localStorage.setItem(FTUE_CACHE_KEY, JSON.stringify(cache));
      }
    } catch {
      // Ignore cache cleanup errors
    }
  }
};

/**
 * Check if user needs to complete FTUE
 */
export const checkFTUERequired = async (
  userId: string,
  token: string
): Promise<boolean> => {
  const cachedStatus = getFTUEFromCache(userId);
  if (cachedStatus !== null) {
    return !cachedStatus;
  }

  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/user/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch user data');
  }

  const userData = await response.json();
  // FTUE is complete once the mic is set up. (interactive_tutorial_completed is a separate future step.)
  const ftueCompleted = userData.mic_setup_completed || false;

  saveFTUEToCache(userId, ftueCompleted);

  return !ftueCompleted;
};

/**
 * Check whether the user has accepted the current policy version.
 * Returns true if accepted, false if acceptance is required.
 */
export const checkPoliciesAccepted = async (
  userId: string,
  token: string
): Promise<boolean> => {
  const currentVersion = await getCurrentPolicyVersion();

  const cachedVersion = getPolicyFromCache(userId);
  if (cachedVersion !== null) {
    return cachedVersion === currentVersion;
  }

  const backendUrl = getBackendUrl();
  const response = await fetch(`${backendUrl}/user/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch user data');
  }

  const userData = await response.json();
  const acceptedVersion: string | null = userData.policy_accepted_version || null;
  if (acceptedVersion) {
    savePolicyToCache(userId, acceptedVersion);
  }

  return acceptedVersion === currentVersion;
};

/**
 * Persist policy acceptance for the current version to the backend and local cache.
 */
export const updatePoliciesAccepted = async (
  userId: string,
  token: string
): Promise<void> => {
  const currentVersion = await getCurrentPolicyVersion();
  const backendUrl = getBackendUrl();
  const updateData: UserPolicyUpdate = {
    policy_accepted_version: currentVersion,
  };

  const response = await fetch(`${backendUrl}/user/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(updateData),
  });

  if (!response.ok) {
    throw new Error('Failed to update policy acceptance status');
  }

  try {
    const userData = await response.json();
    const savedVersion: string | null = userData.policy_accepted_version || null;
    savePolicyToCache(userId, savedVersion ?? currentVersion);
  } catch {
    savePolicyToCache(userId, currentVersion);
  }
};

/**
 * If the browser's IANA timezone differs from what's stored on the profile
 * (stale from signup, or the user has travelled since), sync it to the backend.
 * Used by gamification (streak day-boundary, activity heatmap) to keep the
 * user's "day" aligned with where they actually are.
 */
export const syncUserTimezone = async (
  userId: string,
  token: string,
  storedTimezone: string | null | undefined
): Promise<void> => {
  let browserTimezone: string;
  try {
    browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return;
  }

  if (!browserTimezone || browserTimezone === storedTimezone) {
    return;
  }

  const backendUrl = getBackendUrl();
  try {
    const response = await fetch(`${backendUrl}/user/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ timezone: browserTimezone }),
    });
    if (!response.ok) {
      logger.warn(
        `Timezone sync rejected (${response.status}) — streak/heatmap day boundary stays stale`
      );
    }
  } catch {
    // Best-effort — a failed sync just means the streak/heatmap timezone
    // stays stale until the next successful login, not worth surfacing.
  }
};
